import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { isComboMoveNameMatch } from 'shared-models/browser';
import { orderlinesService, type OrderlineView } from '../../../../../services/orderlines/orderlines.service';

export function getComboMoveTargets(source: OrderlineView, lines: OrderlineView[]) {
  const name = source.productName?.trim().toLowerCase();
  if (!name || source.status !== 'sent' || source.quantity <= 0 || source.productId == null
    || source.unitPrice <= 0 || (source.itemPaidQuantity ?? 0) > 0
    || source.selections?.some((item) => item.groupType === 'combo')) return [];
  return lines.filter((line) => line.id !== source.id && line.orderId === source.orderId
    && line.quantity === 1 && line.unitPrice > 0 && (line.status === 'pending' || line.status === 'sent'))
    .flatMap((line) => (line.selections ?? []).filter((selection) => {
      return selection.groupType === 'combo' && selection.selectionId != null
        && (line.status === 'pending' || selection.sentQuantity !== undefined)
        && (selection.sentQuantity ?? 0) < selection.quantity && isComboMoveNameMatch(source.productName, selection.itemName);
    }).map((selection) => ({ line, selection })))
    .sort((left, right) => Number(right.selection.itemName.trim().toLowerCase() === name)
      - Number(left.selection.itemName.trim().toLowerCase() === name));
}

export function useMoveSentItemIntoCombo({ tenantId, employeeSession, orderId, lines, disabled, lockedLineIds }: {
  tenantId: string; employeeSession: string | null; orderId: number | null;
  lines: OrderlineView[]; disabled: boolean; lockedLineIds: ReadonlySet<number>;
}) {
  const [source, setSource] = useState<{ line: OrderlineView; targetLineId?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  useEffect(() => { setSource(null); }, [orderId]);
  const candidates = (line: OrderlineView) => getComboMoveTargets(line, lines).filter((target) => !lockedLineIds.has(target.line.id));
  const canMove = (line: OrderlineView) => !disabled && !!employeeSession && orderId === line.orderId
    && !lockedLineIds.has(line.id) && candidates(line).length > 0;
  const targets = source ? candidates(source.line).filter((target) => !source.targetLineId || target.line.id === source.targetLineId) : [];
  const open = (line: OrderlineView, targetLineId?: number) => {
    if (!inFlight.current && canMove(line) && (!targetLineId || candidates(line).some((target) => target.line.id === targetLineId))) setSource({ line, targetLineId });
  };
  const confirm = async (selectionId: number) => {
    const target = targets.find((entry) => entry.selection.selectionId === selectionId);
    if (inFlight.current || !source || !target || !employeeSession || orderId == null || !canMove(source.line)) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await orderlinesService.moveIntoCombo(tenantId, orderId, source.line.id, {
        targetOrderlineId: target.line.id, targetSelectionId: selectionId,
        expectedSourceQuantity: source.line.quantity, expectedSentQuantity: target.selection.sentQuantity ?? 0,
      }, employeeSession);
      setSource(null);
    } catch (error) {
      notifications.show({ color: 'red', title: 'העברה נכשלה', message: error instanceof Error ? error.message : 'העברת הפריט לעסקה נכשלה' });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return { source: source?.line ?? null, targets, busy, canMove, open, confirm, close: () => { if (!inFlight.current) setSource(null); } };
}